import { PhoneProvider } from '@compass/client/types/phone_number';

/**
 * User-facing name for a carrier.
 *
 * Carrier names are deliberately NOT shown. Agents choose between networks, not
 * vendors, and the vendor relationship is not something the UI should disclose.
 *
 * The number is the carrier's place in `order`, the global order ops set in dynconfig
 * (BV-2452), so flipping that order changes which carrier is "Provider 1" with no
 * visible change. The order is the same for every program, so a carrier is called the
 * same thing everywhere; numbering by the program's own carriers would make
 * "Provider 1" mean different carriers to different agents.
 *
 * A carrier missing from the order keeps its enum value rather than rendering blank.
 */
export function providerLabel(provider: PhoneProvider, order: PhoneProvider[]): string {
  const rank = order.indexOf(provider);
  return `Provider ${rank === -1 ? provider : rank + 1}`;
}

/**
 * The carrier a call will actually be placed on.
 *
 * A selection the program cannot dial falls back to its first offerable carrier
 * rather than to a fixed default: a program holding only one carrier's numbers has
 * no other to fall back to. `offerable` is in the configured order, so "no selection"
 * (PHONE_PROVIDER_UNSPECIFIED, never offerable) resolves to "Provider 1". Resolved on
 * read rather than corrected in an effect, so there is no window in which the stored
 * selection and the carrier used disagree.
 *
 * Returns undefined when the program has no numbers at all, which the caller must
 * treat as "no carrier to offer" rather than substituting one.
 */
export function resolveActiveProvider(
  selected: PhoneProvider,
  offerable: PhoneProvider[],
): PhoneProvider | undefined {
  if (offerable.includes(selected)) return selected;
  return offerable[0];
}

/**
 * The caller ID for a call on `provider`.
 *
 * Drawn only from that carrier's own numbers, which is the guarantee this module
 * exists for: a carrier rejects a caller ID it does not own, so a number from the
 * wrong group is a failed call rather than a cosmetic mistake.
 *
 * Random rather than round-robin — deliberately. A rotation cursor would have to
 * live in browser storage and would rotate per browser rather than per
 * organisation, which is not meaningfully fairer at this volume.
 *
 * Returns undefined when the carrier has no numbers; the caller must fail before
 * creating a call rather than dial with an empty caller ID.
 */
export function pickCallerId(
  byProvider: Map<PhoneProvider, string[]>,
  provider: PhoneProvider | undefined,
): string | undefined {
  if (provider === undefined) return undefined;
  const numbers = byProvider.get(provider);
  if (!numbers || numbers.length === 0) return undefined;
  return numbers[Math.floor(Math.random() * numbers.length)];
}
